import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * AR Summary RPC Contract
 *
 * Provides a single endpoint returning dashboard-level AR metrics:
 * total outstanding, overdue amounts, invoice counts, DSO, and incoming
 * (not-yet-bank-cleared) remittance dollars.
 * All computed via aggregate queries (no row fetching).
 */

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

/**
 * AR summary response schema — org-scoped dashboard metrics
 */
export const GetArSummaryResponseSchema = z.object({
  /** Sum of balanceDue for all unpaid (non-voided) invoices */
  totalOutstanding: DecimalStringSchema,
  /** Sum of balanceDue where dueDate < today and status not closed/voided */
  overdueAmount: DecimalStringSchema,
  /** Total count of all invoices */
  totalInvoiceCount: z.int().nonnegative(),
  /** Count of invoices with status not in paid/voided */
  openInvoiceCount: z.int().nonnegative(),
  /** Count of invoices where dueDate < today and status not in paid/voided */
  overdueInvoiceCount: z.int().nonnegative(),
  /** Days Sales Outstanding — (totalOutstanding / totalInvoicedLast90) * 90, null if no recent invoices */
  dso: z.number().nullable(),
  /**
   * Sum of stated remittance amounts that customers have notified us about
   * but that haven't been matched to a bank deposit yet — either the
   * remittance has no linked payment yet, or its payment has no
   * bank_transaction_id and is still active.
   */
  incomingPaymentsTotal: DecimalStringSchema,
  /** Count of remittances contributing to incomingPaymentsTotal */
  incomingPaymentsCount: z.int().nonnegative(),
});

/**
 * AR Summary Contracts
 */
export const arSummaryContracts = {
  get: oc.output(GetArSummaryResponseSchema).route({
    method: "GET",
    summary: "Get AR dashboard summary metrics",
    description:
      "Returns organization-wide receivables totals: outstanding and overdue balances, open and overdue invoice counts, days sales outstanding (DSO), and incoming remittances not yet matched to deposits.",
  }),
} as const;

// Type exports
export type GetArSummaryResponse = z.infer<typeof GetArSummaryResponseSchema>;
